// Where a person's notifications go — the tick boxes on their profile (Slack build step 2,
// decision C1 of DESIGN-slack.md), as pure functions so they can be checked without a browser.
//
// ⚠️ THE SERVER IS THE ENFORCEMENT; THIS IS THE POLITE HALF. `services/channel_reach.refusal`
// refuses a choice that can't reach the person, whatever the browser sends. These rules exist
// so the card greys out a box instead of letting somebody click it and be told off — they
// mirror the server's and must never be more permissive than it.
//
// ⚠️ NO REACT AND NO IMPORTS: `notificationChannel.checks.mjs` imports this file directly
// under plain node.

export const CHANNEL_IDS = ['email', 'teams', 'slack'];

// Since Slack build step 2 a stored value is a comma list. "both" is the old word for
// email + Teams, from before tick boxes; it is still read, never written.
export function parseChannels(raw) {
  const found = new Set();
  String(raw || '').split(',').forEach((word) => {
    const w = word.trim().toLowerCase();
    if (w === 'both') { found.add('email'); found.add('teams'); }
    else if (CHANNEL_IDS.includes(w)) found.add(w);
  });
  // ⚠️ The same fallback as the server's channels_for(): nothing readable means email.
  if (!found.size) found.add('email');
  return CHANNEL_IDS.filter((c) => found.has(c));
}

export const asStored = (ticks) => CHANNEL_IDS.filter((c) => ticks.includes(c)).join(',');

// The chat apps that could deliver to this person right now (from /profile/notifications).
export function reachableChats(settings) {
  const out = [];
  if (settings?.teams_installed) out.push('teams');
  if (settings?.slack_found) out.push('slack');
  return out;
}

// Why moving from `before` to `after` would be refused — or '' if it's fine.
// ⚠️ Keep in step with services/channel_reach.refusal: same three rules, same order.
export function refusal(before, after, settings) {
  if (!after.length) return 'Tick at least one place for your notifications to go.';
  if (after.includes('slack') && !before.includes('slack') && !settings?.slack_found) {
    return settings?.slack_connected
      ? 'We haven’t found you in your company’s Slack yet.'
      : 'Your company hasn’t connected HolidayHive to Slack.';
  }
  const reach = reachableChats(settings);
  if (!after.includes('email') && !after.some((c) => reach.includes(c))) {
    return 'None of those can reach you yet, so you’d stop being told about leave altogether.';
  }
  return '';
}

export function toggled(ticks, id) {
  const next = ticks.includes(id) ? ticks.filter((c) => c !== id) : [...ticks, id];
  return CHANNEL_IDS.filter((c) => next.includes(c));
}

// Which rows the card shows. ⚠️ A chat app appears once the company uses it (decision C1)
// — AND whenever it is already ticked, so nobody is left with a choice they can't see or undo.
export function shownChannels(settings, ticks) {
  return CHANNEL_IDS.filter((id) => {
    if (id === 'email') return true;
    if (ticks.includes(id)) return true;
    if (id === 'teams') return !!settings?.teams_available;
    return !!settings?.slack_connected;
  });
}
